import { useState, type FormEvent, type JSX } from 'react';
import { passwordSchema } from '@netdeck/contracts';
import { useTranslation } from '../../shared/i18n';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { Button } from '../../shared/ui/Button';
import { Field } from '../../shared/ui/Field';
import { useChangePassword } from './api';

/**
 * Обязательная смена временного пароля.
 *
 * Показывается вместо приложения, пока `mustChangePassword` не снят: пароль,
 * который знает ещё и администратор, не должен оставаться рабочим.
 *
 * Смена завершает все сессии, включая текущую, поэтому после неё пользователь
 * возвращается на экран входа — это ожидаемое поведение, а не сбой.
 */
export function ChangePasswordPage({ onDone }: { onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const changePassword = useChangePassword();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');

  const mismatch = repeat.length > 0 && next !== repeat;
  const weak = next.length > 0 && !passwordSchema.safeParse(next).success;

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (mismatch || weak) {
      return;
    }
    changePassword.mutate({ currentPassword: current, newPassword: next }, { onSuccess: onDone });
  }

  return (
    <main className="flex min-h-[100dvh] items-center justify-center bg-bg px-6">
      <form
        onSubmit={submit}
        className="flex w-full max-w-[420px] flex-col gap-4 rounded-lg border border-line bg-surface p-7"
      >
        <div className="flex flex-col gap-1.5">
          <h1 className="text-2xl font-semibold tracking-[-0.01em]">{t('auth.change.title')}</h1>
          <p className="text-base text-muted">{t('auth.change.subtitle')}</p>
        </div>

        <Field
          label={t('auth.change.temporary')}
          type="password"
          reveal
          autoComplete="current-password"
          required
          mono
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
        />

        <Field
          label={t('auth.change.newPassword')}
          type="password"
          reveal
          autoComplete="new-password"
          required
          mono
          value={next}
          onChange={(event) => setNext(event.target.value)}
          error={weak ? t('auth.change.tooShort', { min: 12 }) : undefined}
          hint={t('auth.change.minLength', { min: 12 })}
        />

        <Field
          label={t('auth.change.repeat')}
          type="password"
          reveal
          autoComplete="new-password"
          required
          mono
          value={repeat}
          onChange={(event) => setRepeat(event.target.value)}
          error={mismatch ? t('auth.change.mismatch') : undefined}
        />

        {changePassword.error ? (
          <div role="alert" className="text-sm text-down-fg">
            {errorText(changePassword.error)}
          </div>
        ) : null}

        <Button
          icon="lock"
          type="submit"
          variant="primary"
          disabled={changePassword.isPending || mismatch || weak || !current || !next}
        >
          {changePassword.isPending ? t('auth.change.saving') : t('auth.change.submit')}
        </Button>

        <p className="font-mono text-xs text-dim">{t('auth.change.sessionsNote')}</p>
      </form>
    </main>
  );
}
